import { Col, Row, Space, Tag, Timeline } from "antd";
import { ReactNode } from "react";
import TemplateAvatar from "../TemplateAvatar";
import { AWS_REGIONS } from "../../enums/enums";
import AwsLogo from "../../assets/aws-logo.svg";
import KubernetesLogo from "../../assets/kubernetes-icon.svg";
import GitIcon from "../GitIcon";
import { TemplateType } from "../../types";
import { BranchesOutlined } from "@ant-design/icons";

export interface DeployTemplateSummaryProps {
    templateType?: TemplateType;
    appName?: string;
    gitAccount: {
        branch: string;
        fullName: string;
        repositoryUrl: string
    };
    environment: {
        name: string;
        accountId?: string;
        region?: string;
        clusterId?: string;
        clusterNamespace?: string;
    };
    visible?: boolean;
    templateRepositoryUrl: string;
    shouldCreateCluster?: boolean;
    framework?: string;
    footerMessage?: ReactNode;
    clusterAppName?: string;
}

export default function DeployTemplateSummary({
    templateType,
    gitAccount,
    environment,
    appName,
    templateRepositoryUrl,
    shouldCreateCluster,
    visible,
    framework,
    footerMessage,
    clusterAppName
}: DeployTemplateSummaryProps) {

    return (
        <>
            {visible &&
                <>
                    <div className="text-center">
                        <Space>
                            <TemplateAvatar name={framework!} />
                            <span className="deploy-template-summary-step-title">
                                {appName!}
                            </span>
                        </Space>
                        <div>Review your {templateType === "cluster" ? "app" : "resource"} deployment</div>
                    </div>
                    <br />
                    <br />
                    <Timeline mode="alternate">
                        {shouldCreateCluster &&
                            [<Timeline.Item key="create-resource">
                                <span className="deploy-template-summary-step-title">
                                    Create resource
                                </span>
                                <div>
                                    {clusterAppName}
                                </div>
                                <div>
                                    Environment: <Tag>{environment.name}</Tag>
                                </div>
                                <div>
                                    <img src={AwsLogo} style={{ width: "24px" }} alt="aws" /> &nbsp;{environment.accountId}
                                    &nbsp;-&nbsp;{AWS_REGIONS.find(region => region.id === environment.region)?.name}
                                </div>
                            </Timeline.Item>,
                            <Timeline.Item key="app-deployment">
                                <span className="deploy-template-summary-step-title">
                                    Proceed with app deployment
                                </span>
                            </Timeline.Item>
                            ]
                        }
                        <Timeline.Item key="fetch-from-repo">
                            <span className="deploy-template-summary-step-title">
                                Fetch from repo
                            </span>
                            {gitAccount?.repositoryUrl ?
                                <div>
                                    <div>
                                        <GitIcon repoUrl={gitAccount.repositoryUrl} />&nbsp;<a href={gitAccount.repositoryUrl} target="_blank" rel="noreferrer">{gitAccount.fullName}</a>
                                        {gitAccount.branch &&
                                            <div>
                                                <BranchesOutlined /> {gitAccount.branch}
                                            </div>
                                        }
                                    </div>
                                </div> :
                                <div>
                                    <a href={templateRepositoryUrl} target="_blank" rel="noreferrer">
                                        {templateRepositoryUrl}
                                    </a>
                                </div>
                            }
                        </Timeline.Item>
                        {
                            environment &&
                            <Timeline.Item key="create-app-resource-in-environment">
                                <span className="deploy-template-summary-step-title">
                                    Create {templateType === "cluster" ? "app" : "resource"} in environment
                                </span>
                                <div>
                                    {environment.name}
                                </div>
                            </Timeline.Item>
                        }
                        {
                            ((environment.clusterId && environment.clusterNamespace) || shouldCreateCluster) &&
                            <Timeline.Item key="deploy-in-cluster">
                                <span className="deploy-template-summary-step-title flex-align-center">
                                    <img src={KubernetesLogo} style={{ width: "20px" }} alt="aws" /> &nbsp; Deploy in cluster
                                </span>
                                <div>
                                    {shouldCreateCluster ? clusterAppName : environment.clusterId}
                                </div>
                                <div>
                                    Namespace:&nbsp;
                                    <Tag>
                                        {shouldCreateCluster ? "microtica" : environment.clusterNamespace}
                                    </Tag>
                                </div>
                            </Timeline.Item>
                        }
                        {templateType === "environment" &&
                            <Timeline.Item key="aws-account">
                                <span className="deploy-template-summary-step-title">
                                    <img src={AwsLogo} style={{ width: "24px" }} alt="aws" /> &nbsp; Provision infrastructure
                                </span>
                                <div>
                                    {environment.accountId}
                                </div>
                                <div>
                                    {AWS_REGIONS.find(region => region.id === environment.region)?.name}
                                </div>
                            </Timeline.Item>
                        }
                    </Timeline>
                    <br />
                </>
            }
            <Row gutter={24}>
                <Col xs={24} style={{ textAlign: "center" }}>
                    {footerMessage}
                </Col>
            </Row>
        </>
    )
}